import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { withCreds } from "../authService";
import { markLoginLatency, startLoginLatencyTrace } from "../lib/loginLatency";
import { CelestialShell, SystemFooter } from "./ui/CelestialShell";

export const AUTHENTICATED_HOME_PATH = "/chat";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { setAuthState } = useAuth();

  async function handleSignIn(e) {
    e.preventDefault();
    setMsg("");
    setLoading(true);
    startLoginLatencyTrace();

    try {
      const u = (username || "").trim();
      const p = (password || "").trim();

      if (!u || !p) {
        setMsg("Enter username and password.");
        return;
      }

      markLoginLatency('auth_request_dispatched');
      const loginRes = await withCreds("/api/login", {
        method: "POST",
        body: JSON.stringify({ username: u, password: p }),
      });
      markLoginLatency('auth_response_received');

      let loginData = {};
      try {
        loginData = await loginRes.json();
      } catch {
        loginData = {};
      }

      if (!loginRes.ok) {
        const errorMsg =
          loginData?.error ||
          loginData?.message ||
          `HTTP ${loginRes.status}`;
        setMsg(`Login failed: ${errorMsg}`);
        return;
      }

      const isAuthed =
        loginData?.authenticated === true ||
        loginData?.ok === true;

      if (!isAuthed) {
        setMsg("Login didn’t complete. Try again.");
        return;
      }

      const user = {
        authenticated: true,
        success: true,
        sub: loginData?.sub || u,
        username: loginData?.username || u,
        roles: Array.isArray(loginData?.roles) ? loginData.roles : ["user"],
      };

      markLoginLatency('session_established');

      try {
        sessionStorage.setItem(
          "br_login_grace_until",
          String(Date.now() + 15000)
        );
      } catch {
        // ignore
      }

      setAuthState((prev) => ({
        ...prev,
        isAuthenticated: true,
        user,
        loading: false,
        error: null,
        initialized: true,
      }));

      markLoginLatency('account_bootstrap_complete');
      markLoginLatency('route_transition_started');

      navigate(AUTHENTICATED_HOME_PATH, { replace: true });
    } catch (err) {
      setMsg(err?.message || "Network error. Please retry.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <CelestialShell>
      <div className="br-container">
        <main className="br-auth-layout">
          <section className="br-auth-story">
            <p className="br-eyebrow">Welcome back</p>
            <h1 className="br-page-title">Your space is<br />still here.</h1>
            <p className="br-lead">Return to the conversations, reflections, and moments you chose to keep. Nothing begins until you do.</p>
          </section>

          <section className="br-auth-card br-glass">
            <header className="br-auth-card__header">
              <p className="br-eyebrow">Private session</p>
              <h2 className="br-auth-card__title">Ready when you are.</h2>
              <p className="br-auth-card__copy">Enter your details to restore your BrightResolve space.</p>
            </header>
            <form onSubmit={handleSignIn} className="br-auth-form">
              <div className="br-field">
                <label htmlFor="br-login-username">Username</label>
                <input id="br-login-username" type="text" aria-label="Username" autoComplete="username" placeholder="Your username" value={username} onChange={(e) => setUsername(e.target.value)} disabled={loading} className="br-input" />
              </div>
              <div className="br-field">
                <label htmlFor="br-login-password">Password</label>
                <input id="br-login-password" type="password" aria-label="Password" placeholder="Your password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} className="br-input" />
              </div>
              {msg && <p className="br-status br-status--error" role="alert">{msg}</p>}
              <button type="submit" disabled={loading} className="br-button br-button--primary">
                {loading ? "Opening your space..." : "Enter BrightResolve"}
              </button>
              <div className="br-auth-links">
                <span><Link to="/recover">Forgot password?</Link></span>
                <Link to="/">Return home</Link>
              </div>
            </form>
          </section>
        </main>
        <SystemFooter />
      </div>
    </CelestialShell>
  );
}
